import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader, { DarkPage } from "@/components/app/PageHeader";
import {
  HeartPulse, TrendingDown, Wallet, CalendarClock, Users, GitPullRequestArrow,
  MessageCircle, ArrowRight, Info, ChevronDown,
} from "lucide-react";

const BAND_COLOR = { green: "#00C896", amber: "#F59E0B", red: "#FF007A", unscored: "#6B7280" };
const BAND_DOT = { green: "bg-[#00C896]", amber: "bg-[#F59E0B]", red: "bg-[#FF007A]", unscored: "bg-[#6B7280]" };
const STATUS_ICON = { healthy: "🟢", at_risk: "🟠", intervention: "🔴", unscored: "⚪" };
const STATUS_LABEL = {
  healthy: "healthy", at_risk: "at risk", intervention: "requiring intervention", unscored: "not yet scored",
};

const DIMENSION_META = {
  budget:    { label: "Budget",    icon: Wallet },
  schedule:  { label: "Schedule",  icon: CalendarClock },
  resources: { label: "Resources", icon: Users },
  scope:     { label: "Scope",     icon: GitPullRequestArrow },
  client:    { label: "Client sentiment", icon: MessageCircle },
};

const fmtGBP = (n) => `£${Math.round(n || 0).toLocaleString()}`;

// ─── Score ring ───────────────────────────────────────────────────────────
const ScoreRing = ({ score, band, size = 168 }) => {
  const r = (size - 14) / 2;
  const c = 2 * Math.PI * r;
  const pct = score == null ? 0 : Math.max(0, Math.min(100, score)) / 100;
  const color = BAND_COLOR[band] || "#6B7280";
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#1F2937" strokeWidth="12" />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="12"
        strokeDasharray={c} strokeDashoffset={c * (1 - pct)} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dashoffset 0.6s ease" }}
      />
      <text x="50%" y="47%" textAnchor="middle" fontSize={score == null ? "22" : "40"} fontWeight="800" fill="#FFFFFF" fontFamily="inherit">
        {score == null ? "No data" : Math.round(score)}
      </text>
      {score != null && (
        <text x="50%" y="64%" textAnchor="middle" fontSize="12" fill="#6B7280" letterSpacing="2">
          / 100
        </text>
      )}
    </svg>
  );
};

// ─── Dimension row (drill-down) ─────────────────────────────────────────────
const DimensionRow = ({ dimKey, dim, focused, onFocus }) => {
  const meta = DIMENSION_META[dimKey];
  const Icon = meta.icon;
  const color = BAND_COLOR[dim.band];
  const unscored = !dim.scored;
  return (
    <button
      onClick={() => onFocus(dimKey)}
      className={`w-full text-left px-4 py-3 border-b border-[#1F2937] last:border-b-0 hover:bg-[#0a0f1a] transition-colors ${
        focused === dimKey ? "bg-[#0a0f1a]" : ""
      }`}
      data-testid={`hs-dimension-${dimKey}`}
    >
      <div className="flex items-center gap-3">
        <Icon className="w-4 h-4 text-[#6B7280] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-white">{meta.label}</span>
            <span className="flex items-center gap-1.5">
              <span className={`inline-block w-2 h-2 rounded-full ${BAND_DOT[dim.band]}`} />
              <span className="font-mono text-sm font-semibold" style={{ color }}>
                {unscored ? "—" : dim.score}
              </span>
            </span>
          </div>
          <div className="h-1 bg-[#1F2937] mt-2 w-full">
            {!unscored && <div className="h-full" style={{ width: `${dim.score}%`, backgroundColor: color }} />}
          </div>
          {unscored ? (
            <div className="text-[10px] text-[#6B7280] mt-1.5 leading-snug italic">Not scored yet — {dim.reason}</div>
          ) : (
            <div className="text-[10px] text-[#4B5563] mt-1.5 leading-snug">{dim.data_source}</div>
          )}
        </div>
      </div>
    </button>
  );
};

// ─── Drill-down panel for one project ───────────────────────────────────────
const ProjectDrillDown = ({ project }) => {
  const [focused, setFocused] = useState(null);
  if (!project) return null;
  const b = project.dimensions.budget;

  return (
    <div className="border border-[#1F2937] bg-[#111827]" data-testid="hs-drilldown">
      <div className="border-b border-[#1F2937] px-6 py-4 flex flex-wrap items-center gap-3">
        <span className={`inline-block w-2.5 h-2.5 rounded-full ${BAND_DOT[project.band]}`} />
        <Link to={`/workspace/${project.project_id}`} className="font-display font-bold tracking-tight text-lg text-white hover:text-[#00F5D4] transition-colors">
          {project.name}
        </Link>
        <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border" style={{ borderColor: BAND_COLOR[project.band], color: BAND_COLOR[project.band] }}>
          {STATUS_LABEL[project.status]}
        </span>
        <span className="ml-auto font-mono text-2xl font-black" style={{ color: BAND_COLOR[project.band] }}>
          {project.score ?? "—"}<span className="text-sm text-[#6B7280]">/100</span>
        </span>
      </div>
      {project.unscored_dimensions.length > 0 && (
        <div className="px-6 py-2 border-b border-[#1F2937] bg-[#0a0f1a] text-[11px] text-[#6B7280]">
          Score based on {project.scored_weight_pct}% of the weighting model —{" "}
          {project.unscored_dimensions.map((d) => DIMENSION_META[d].label).join(", ")} not yet scored.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#1F2937]">
        {/* Profitability */}
        <div className="bg-[#111827] p-6">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280] mb-4">Project profitability</div>
          {!b.scored ? (
            <div className="space-y-3">
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-[#9CA3AF]">Revenue</span>
                <span className="font-mono text-lg text-white">{fmtGBP(project.revenue)}</span>
              </div>
              <div className="border border-[#1F2937] bg-[#0a0f1a] p-3 text-xs text-[#6B7280] italic">
                Cost and margin not scored yet — {b.reason}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-[#9CA3AF]">Revenue</span>
                <span className="font-mono text-lg text-white">{fmtGBP(project.revenue)}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-[#9CA3AF]">
                  Forecast cost <span className="text-[#4B5563]">(at completion, not cost-to-date)</span>
                </span>
                <span className="font-mono text-lg text-white">{fmtGBP(b.forecast_cost_at_completion)}</span>
              </div>
              <div className="flex items-baseline justify-between border-t border-[#1F2937] pt-3">
                <span className="text-xs text-[#9CA3AF]">Forecast margin</span>
                <span className="font-mono text-lg font-semibold" style={{ color: BAND_COLOR[b.band] }}>
                  {fmtGBP(project.revenue - b.forecast_cost_at_completion)}
                  <span className="text-xs ml-1.5 text-[#6B7280]">({b.forecast_margin_pct}%)</span>
                </span>
              </div>
              <div className="flex items-baseline justify-between text-[11px] text-[#6B7280]">
                <span>
                  vs target margin {b.target_margin_pct}%
                  {project.target_margin_is_default && <span className="text-[#4B5563]"> (assumed default)</span>}
                </span>
                <span className={b.gap_pts < 0 ? "text-[#FF007A]" : "text-[#00C896]"}>
                  {b.gap_pts >= 0 ? "+" : ""}{b.gap_pts} pts
                </span>
              </div>
              <div className="text-[11px] text-[#6B7280]">Cost to date: {fmtGBP(b.cost_to_date)} · burn {fmtGBP(project.weekly_burn)}/week</div>
            </div>
          )}

          {(project.insight.budget_headline || project.insight.budget_recovery || project.insight.scope_clawback) && (
            <div className="mt-5 border border-[#1F2937] bg-[#0a0f1a] p-4 space-y-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#00F5D4] flex items-center gap-1.5">
                <Info className="w-3 h-3" /> Arithmetic, not a prediction
              </div>
              {project.insight.budget_headline && (
                <div className="text-xs text-[#D1D5DB] leading-relaxed">{project.insight.budget_headline}</div>
              )}
              {project.insight.budget_recovery && (
                <div className="text-xs text-[#9CA3AF] leading-relaxed">{project.insight.budget_recovery}</div>
              )}
              {project.insight.scope_clawback && (
                <div className="text-xs text-[#9CA3AF] leading-relaxed">{project.insight.scope_clawback}</div>
              )}
            </div>
          )}
        </div>

        {/* Delivery risk */}
        <div className="bg-[#111827]">
          <div className="px-6 pt-6 text-[11px] uppercase tracking-[0.25em] text-[#6B7280] mb-1">Delivery risk</div>
          <div>
            {Object.entries(project.dimensions).map(([k, dim]) => (
              <DimensionRow key={k} dimKey={k} dim={dim} focused={focused} onFocus={setFocused} />
            ))}
          </div>
        </div>
      </div>

      {/* What's pulling the score down */}
      {project.pulling_down.length > 0 && (
        <div className="border-t border-[#1F2937] px-6 py-4">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280] mb-3">What's pulling the score down</div>
          <div className="flex flex-wrap gap-2">
            {project.pulling_down.map((d) => (
              <button
                key={d.dimension}
                onClick={() => setFocused(d.dimension)}
                className="flex items-center gap-2 px-3 py-1.5 border text-xs transition-colors hover:bg-[#1a2535]"
                style={{ borderColor: `${BAND_COLOR[d.band]}55`, color: BAND_COLOR[d.band] }}
                data-testid={`hs-pulling-${d.dimension}`}
              >
                <TrendingDown className="w-3 h-3" />
                {DIMENSION_META[d.dimension].label} · −{d.points_lost.toFixed(1)} pts
                <span className="text-[10px] text-[#6B7280]">({Math.round(d.weight * 100)}% weight)</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Company header ──────────────────────────────────────────────────────────
const CompanyHeader = ({ company, weights, thresholds }) => {
  const [showWeights, setShowWeights] = useState(false);
  return (
    <div className="border border-[#1F2937] bg-[#111827]">
      <div className="p-6 sm:p-8 flex flex-col md:flex-row items-center md:items-start gap-8">
        <ScoreRing score={company.score} band={company.band} />
        <div className="flex-1 w-full">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280]">Company delivery health</div>
          <div className="flex items-baseline gap-3 mt-1">
            <span className="font-display font-black text-4xl tracking-tighter" style={{ color: BAND_COLOR[company.band] }}>
              {company.score ?? "—"}
            </span>
            <span className="text-sm text-[#6B7280]">/ 100</span>
            {company.capped_by_red_project && (
              <span className="text-[11px] text-[#FF007A] border border-[#FF007A]/30 px-2 py-0.5">
                capped at {85} — a project needs intervention
              </span>
            )}
          </div>
          <div className="text-[11px] text-[#4B5563] mt-1">
            Weighted by fee across {company.scored_projects} scored project{company.scored_projects === 1 ? "" : "s"}
            {company.scored_projects !== company.total_projects && ` of ${company.total_projects} total`} — not a plain average.
          </div>

          <div className="flex flex-wrap gap-3 mt-5">
            <div className="flex items-center gap-2 px-3 py-2 border border-[#1F2937]">
              <span>{STATUS_ICON.healthy}</span>
              <span className="font-mono font-bold text-white">{company.counts.healthy}</span>
              <span className="text-xs text-[#9CA3AF]">projects healthy</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 border border-[#1F2937]">
              <span>{STATUS_ICON.at_risk}</span>
              <span className="font-mono font-bold text-white">{company.counts.at_risk}</span>
              <span className="text-xs text-[#9CA3AF]">at risk</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 border border-[#1F2937]">
              <span>{STATUS_ICON.intervention}</span>
              <span className="font-mono font-bold text-white">{company.counts.intervention}</span>
              <span className="text-xs text-[#9CA3AF]">requiring intervention</span>
            </div>
            {company.counts.unscored > 0 && (
              <div className="flex items-center gap-2 px-3 py-2 border border-[#1F2937]">
                <span>{STATUS_ICON.unscored}</span>
                <span className="font-mono font-bold text-white">{company.counts.unscored}</span>
                <span className="text-xs text-[#9CA3AF]">not yet scored</span>
              </div>
            )}
          </div>

          <button
            onClick={() => setShowWeights((v) => !v)}
            className="flex items-center gap-1 text-[11px] text-[#6B7280] hover:text-[#00F5D4] mt-5 transition-colors"
            data-testid="hs-show-weights"
          >
            <ChevronDown className={`w-3 h-3 transition-transform ${showWeights ? "rotate-180" : ""}`} />
            How this score is calculated
          </button>
          {showWeights && (
            <div className="mt-3 text-xs text-[#9CA3AF] space-y-1.5 border-t border-[#1F2937] pt-3">
              <div>
                Weights: {Object.entries(weights).map(([k, w]) => `${DIMENSION_META[k].label} ${Math.round(w * 100)}%`).join(" · ")}
              </div>
              <div>
                Thresholds: green ≥{thresholds.green_min}, amber {thresholds.amber_min}–{thresholds.green_min - 1}, red &lt;{thresholds.amber_min}.
              </div>
              <div>
                One red dimension forces a project to at least "at risk." A score under {thresholds.amber_min}, or two red
                dimensions, forces "requiring intervention" — even if the fee-weighted average alone wouldn't.
              </div>
              <div className="text-[#4B5563]">
                No probabilities are generated here — every figure is arithmetic over Xero-synced cost, timesheets, the
                resource plan, change requests and stakeholder engagement. Weights are the thing to validate against real
                projects that blew their budget.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────
const HealthScore = () => {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/health-score/portfolio")
      .then((res) => {
        setData(res.data);
        setSelected(res.data.projects?.[0]?.project_id || null);
      })
      .catch((e) => setError(e.response?.data?.detail || "Could not load health score"));
  }, []);

  const selectedProject = useMemo(
    () => data?.projects?.find((p) => p.project_id === selected) || null,
    [data, selected]
  );

  if (error) {
    return (
      <DarkPage>
        <PageHeader eyebrow="Insights & AI" title="Project Health Score" />
        <div className="px-5 sm:px-8 lg:px-12 py-10 text-sm text-[#FF007A]">{error}</div>
      </DarkPage>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="text-xs uppercase tracking-[0.25em] text-[#6B7280]">Scoring the portfolio…</div>
      </div>
    );
  }

  return (
    <DarkPage>
      <PageHeader
        eyebrow="Insights & AI"
        title="Project Health Score"
        description="One deterministic scoring engine — every number reconciles, every dimension shows its data source."
      />

      <div className="px-5 sm:px-8 lg:px-12 py-8 space-y-8">
        <CompanyHeader company={data.company} weights={data.weights} thresholds={data.thresholds} />

        <div className="grid grid-cols-1 xl:grid-cols-[340px_1fr] gap-6 items-start">
          {/* Project list */}
          <div className="border border-[#1F2937] bg-[#111827]" data-testid="hs-project-list">
            <div className="border-b border-[#1F2937] px-4 py-3 flex items-center gap-2">
              <HeartPulse className="w-4 h-4 text-[#00F5D4]" />
              <div className="font-display font-bold tracking-tight text-sm text-white">Projects</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] ml-auto">{data.projects.length}</div>
            </div>
            <div className="max-h-[640px] overflow-y-auto">
              {data.projects.map((p) => (
                <button
                  key={p.project_id}
                  onClick={() => setSelected(p.project_id)}
                  className={`w-full text-left px-4 py-3 border-b border-[#1F2937] last:border-b-0 hover:bg-[#0a0f1a] transition-colors flex items-center gap-3 ${
                    selected === p.project_id ? "bg-[#0a0f1a] border-l-2 border-l-[#00F5D4]" : ""
                  }`}
                  data-testid={`hs-project-row-${p.project_id}`}
                >
                  <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${BAND_DOT[p.band]}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">{p.name}</div>
                    <div className="text-[10px] text-[#6B7280]">{STATUS_LABEL[p.status]}</div>
                  </div>
                  <span className="font-mono text-sm font-semibold" style={{ color: BAND_COLOR[p.band] }}>{p.score ?? "—"}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-[#374151] shrink-0" />
                </button>
              ))}
              {data.projects.length === 0 && (
                <div className="px-4 py-8 text-center text-xs text-[#6B7280]">No live projects to score yet.</div>
              )}
            </div>
          </div>

          {/* Drill-down */}
          <ProjectDrillDown project={selectedProject} />
        </div>
      </div>
    </DarkPage>
  );
};

export default HealthScore;
